import {
  Header,
  Sidenav,
  type SidenavNavItem,
  AccessGatewayIcon,
  AdminIcon,
  DashboardIcon,
  GovernIcon,
  IdentitiesIcon,
  IntegrationsIcon,
  PolicyIcon,
} from "@repo/ui";
import { tenantApi, storeTokens } from "@repo/api";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { ReactNode, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useAuth } from "../contexts/AuthContext";
import {
  PermissionStateContext,
  type PermissionState,
} from "../contexts/PermissionStateContext";
import { useTheme } from "../contexts/ThemeContext";
import { useViewport, ViewportProvider } from "../contexts/ViewportContext";
import { useTrialStatus } from "../hooks/useTrialStatus";
import { DEMO_DAY0_KEY } from "../utils/day0";
import { useGetCurrentUserRoles } from "./hooks/useGetCurrentUserRoles";
import { navLabels } from "./navLabels";

interface ShellLayoutProps {
  children: ReactNode;
}

function ShellLayoutInner({ children }: ShellLayoutProps) {
  const { logout, userDisplayName, userLoginEmail } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const intl = useIntl();
  const {
    isMenuPinned,
    isMenuExpanded,
    setMenuPinned,
    setMenuExpanded,
    headerRef,
  } = useViewport();
  const { location } = useRouterState();
  // `/` is the Day-0 landing — full-page chrome (header only, no sidenav).
  const isDay0Landing = location.pathname === "/";

  const { data, isLoading } = useGetCurrentUserRoles();
  const isAdmin = data?.roles?.includes("ROLE_SAV_ISPMADMIN") ?? false;

  const { data: tenantsData } = useQuery({
    queryKey: ["tenants"],
    queryFn: () => tenantApi.getTenants(),
    staleTime: 5 * 60 * 1000,
  });

  // The active tenant is read straight from localStorage: it only ever changes
  // via a full reload on switch (see the mutation below), so a non-reactive
  // read during render is correct.
  const storedTenantId = localStorage.getItem("tenantId") ?? undefined;
  const tenantList = tenantsData?.tenants ?? [];
  const currentTenantId =
    (storedTenantId &&
    tenantList.some((t) => String(t.tenantId) === storedTenantId)
      ? storedTenantId
      : undefined) ?? tenantsData?.primaryTenantId;

  const currentTenant = tenantsData?.tenants.find(
    (t) => t.tenantId === currentTenantId,
  );

  const permissionState = useMemo<PermissionState>(() => {
    const isReadOnly = isLoading ? true : !isAdmin;
    const permissions = data?.permissions ?? [];
    const userRole =
      data?.roles?.find((r: string) => r.startsWith("ROLE_SAV_ISPM")) ?? "";
    const license = currentTenant
      ? {
          licenseType: currentTenant.licenseType,
          expiresAt: currentTenant.expiresAt,
          tenantId: currentTenant.tenantId,
          tenantName: currentTenant.name,
        }
      : undefined;

    return { isReadOnly, permissions, userRole, license };
  }, [data, isAdmin, isLoading, currentTenant]);

  const trialStatus = useTrialStatus(
    currentTenant?.expiresAt,
    currentTenant?.licenseType,
  );
  // Trial ticker: surfaces when expired OR within 7 days of expiry, always in
  // red (critical) to signal urgency. Hidden otherwise.
  const trial =
    trialStatus != null && (trialStatus.isExpired || trialStatus.daysLeft <= 7)
      ? {
          label: trialStatus.isExpired
            ? intl.formatMessage({
                id: "SDz0dG",
                defaultMessage: "Trial Expired",
                description:
                  "Header chip shown when the trial license has expired",
              })
            : intl.formatMessage(
                {
                  id: "I0fAue",
                  defaultMessage: "{days} days left",
                  description: "Trial countdown chip in the header",
                },
                { days: trialStatus.daysLeft },
              ),
          tone: "critical" as const,
        }
      : undefined;

  const [isSwitchingTenant, setIsSwitchingTenant] = useState(false);

  const { mutate: switchTenant } = useMutation({
    mutationFn: (tenantId: string) => tenantApi.selectTenant(tenantId),
    onMutate: () => {
      setIsSwitchingTenant(true);
    },
    onSuccess: (response) => {
      // 1) Tokens FIRST — synchronous localStorage writes, before the reload so
      //    the host and every MFE boot with the new tenant's token.
      storeTokens({
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
      });

      // 2) Persist the newly selected tenant so it is active after the reload.
      localStorage.setItem("tenantId", response.selectedTenantId);

      // ── DEMO_DAY0 override (QA/demo) ───────────────────────────────────
      // With `localStorage.setItem("DEMO_DAY0", "1")` set, send the switch
      // straight to the Day-0 landing at "/", which forces Day-0 to render
      // regardless of the new tenant's lastAccessedAt (see utils/day0). Clear
      // the flag (`removeItem`) to restore the normal reload.
      if (localStorage.getItem(DEMO_DAY0_KEY) === "1") {
        window.location.assign("/");
        return;
      }
      // ───────────────────────────────────────────────────────────────────

      // 3) Full reload — the only host-only way to guarantee NO stale data
      //    across every MFE. Each MFE owns its QueryClient and its own error
      //    rendering (on a failed refetch React Query keeps the last data, so
      //    the MFE would otherwise show the previous tenant's rows). A reboot
      //    drops all caches + component state and refetches for the new tenant;
      //    a failed fetch then shows the MFE's own empty/error state. Requires
      //    no MFE changes. isSwitchingTenant stays true — the page unmounts.
      window.location.reload();
    },
    onError: () => {
      // ── DEMO_DAY0 override (QA/demo) ───────────────────────────────────
      // On localhost the tenant-select API may fail; still honour the override
      // and show Day-0 regardless of the backend response.
      if (localStorage.getItem(DEMO_DAY0_KEY) === "1") {
        window.location.assign("/");
        return;
      }
      // ───────────────────────────────────────────────────────────────────
      setIsSwitchingTenant(false);
    },
  });

  const navItems = useMemo<SidenavNavItem[]>(
    () => [
      {
        navItem: "dashboard",
        label: intl.formatMessage(navLabels.dashboard),
        icon: <DashboardIcon />,
        section: "workspace",
      },
      {
        navItem: "identities",
        label: intl.formatMessage(navLabels.identities),
        icon: <IdentitiesIcon />,
        section: "workspace",
        path: "/identities",
      },
      {
        navItem: "policies",
        label: intl.formatMessage(navLabels.policies),
        icon: <PolicyIcon />,
        section: "workspace",
        path: "/policies",
      },
      {
        navItem: "integrations",
        label: intl.formatMessage(navLabels.integrations),
        icon: <IntegrationsIcon />,
        section: "workspace",
      },
      {
        navItem: "lifecycle",
        label: intl.formatMessage(navLabels.lifecycle),
        icon: (
          <span className="inline-flex scale-[1.2]">
            <GovernIcon />
          </span>
        ),
        section: "workspace",
        subNav: [
          {
            subNavItem: "agents",
            label: intl.formatMessage(navLabels.agents),
            path: "/agents",
          },
          {
            subNavItem: "non-human",
            label: intl.formatMessage(navLabels["non-human"]),
            path: "/nhi-olcm",
          },
        ],
      },
      {
        navItem: "protect",
        label: intl.formatMessage(navLabels.protect),
        icon: (
          <span className="inline-flex scale-[1.2]">
            <AccessGatewayIcon />
          </span>
        ),
        section: "workspace",
        subNav: [
          {
            subNavItem: "access-gateway",
            label: intl.formatMessage(navLabels["access-gateway"]),
            path: "/agent-gateway",
          },
          {
            subNavItem: "agent-runtime-authz",
            label: intl.formatMessage(navLabels["agent-runtime-authz"]),
            path: "/agent-runtime-authz",
            badge: "Beta",
          },
        ],
      },
      ...(isAdmin
        ? [
            {
              navItem: "admin",
              label: intl.formatMessage(navLabels.admin),
              icon: <AdminIcon />,
              section: "system" as const,
              subNav: [
                {
                  subNavItem: "audit-trail",
                  label: intl.formatMessage(navLabels["audit-trail"]),
                },
                {
                  subNavItem: "ispm-users",
                  label: intl.formatMessage(navLabels["ispm-users"]),
                },
                {
                  subNavItem: "settings",
                  label: intl.formatMessage(navLabels.settings),
                  children: [
                    {
                      subNavItem: "sso-settings",
                      label: intl.formatMessage(navLabels["sso-settings"]),
                    },
                  ],
                },
              ],
            },
          ]
        : []),
    ],
    [intl, isAdmin],
  );

  if (isDay0Landing) {
    return (
      <PermissionStateContext.Provider value={permissionState}>
        <div className="flex min-h-screen flex-col bg-[var(--color-page-subtle,#f2f4f8)]">
          <div
            ref={headerRef}
            className="sticky top-0 z-[1008] shrink-0 bg-[var(--color-background-neutral-subtlest)]"
          >
            <Header
              theme={theme}
              onToggleTheme={toggleTheme}
              onLogout={logout}
              user={{
                displayName: userDisplayName ?? undefined,
                email: userLoginEmail ?? undefined,
              }}
              showLogo
              showHeaderActions={false}
              tenants={tenantsData?.tenants}
              currentTenantId={currentTenantId}
              onTenantSelect={switchTenant}
              isSwitchingTenant={isSwitchingTenant}
              trial={trial}
            />
          </div>
          <main className="flex-1 min-w-0">{children}</main>
        </div>
      </PermissionStateContext.Provider>
    );
  }

  return (
    <PermissionStateContext.Provider value={permissionState}>
      <div className="min-h-screen flex bg-page-bg">
        <div className="sticky top-0 self-start h-screen shrink-0">
          <Sidenav
            navItems={navItems}
            isLoading={isLoading}
            isMenuPinned={isMenuPinned}
            onPinnedChange={setMenuPinned}
            isMenuExpanded={isMenuExpanded}
            onExpandedChange={setMenuExpanded}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col bg-[var(--color-page-subtle)]">
          <div
            ref={headerRef}
            className="sticky top-0 z-[1008] shrink-0 bg-[var(--color-background-neutral-subtlest)]"
          >
            <Header
              theme={theme}
              onToggleTheme={toggleTheme}
              onLogout={logout}
              user={{
                displayName: userDisplayName ?? undefined,
                email: userLoginEmail ?? undefined,
              }}
              showLogo={!(isMenuPinned || isMenuExpanded)}
              showHeaderActions={false}
              tenants={tenantsData?.tenants}
              currentTenantId={currentTenantId}
              onTenantSelect={switchTenant}
              isSwitchingTenant={isSwitchingTenant}
              trial={trial}
            />
          </div>
          <main className="flex-1 min-w-0">{children}</main>
        </div>
      </div>
    </PermissionStateContext.Provider>
  );
}

export function ShellLayout({ children }: ShellLayoutProps) {
  return (
    <ViewportProvider>
      <ShellLayoutInner>{children}</ShellLayoutInner>
    </ViewportProvider>
  );
}
